import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { EnergyDataService } from '../../_shared/services/energy-data.service';
import { Globals } from '../../_shared/services/globals';
import { MonitoringService } from '../../_shared/services/monitoring.service';

interface InputSeries {
    data: number[];
    color: string;
    title: string;
}

interface ProfileData {
    date: string;
    value: string;
}

@Component({
    selector: 'island-energy',
    templateUrl: './island-energy.component.html',
    styleUrls: ['./island-energy.component.css']
})
export class IslandEnergyComponent implements OnInit {
    // Configuration for the charts
    energyChartData: InputSeries[] = [];
    priceChartData: InputSeries[] = [];

    // loaded
    energyData: number[] = [];
    solarData: number[] = [];
    apxPriceData: number[] = [];

    // Calculated
    gridData: number[] = [];
    balanceData: number[] = [];

    // Static data
    labelData: string[] = [];
    islandPriceData: number[] = [];

    // Properties for UI
    currentIslandChart: string = "";
    potentialSavings: number = 0;
    directApxBuy: number = 0;
    renewable: number = 0;
    energyProduced: number = 0;
    energyConsumed: number = 0;

    // Text
    islandName: string = "Energie Eiland";

    constructor(
        public globals: Globals,
        private energyDataService: EnergyDataService,
        private router: Router,
        monitoringService: MonitoringService) {
        monitoringService.logPageView();

        this.initStaticData();
    }

    ngOnInit() {
        if (this.mobileCheck()) {
            this.router.navigate(['./dashboard']);
        } else {
            this.energyDataService.dataLoadedEvent.subscribe((dataset: string) => this.refreshData());
            this.refreshData();
        }
    }

    loadIslandData() {
        this.islandName = "";
        let energySource = this.energyDataService.islandEnergyProfile;
        let solarSource = this.energyDataService.islandSolarProfile;
        if (!this.globals.demoData) {
            energySource = this.energyDataService.islandEnergyActual;
            solarSource = this.energyDataService.islandSolarActual;
            if (this.energyDataService.participantInfo) {
                this.islandName = this.energyDataService.participantInfo.islandName;
            }
        }
        else {
            this.islandName = "Demo Eiland";
        }

        if (energySource || solarSource) {
            if (energySource) {
                this.energyData = this.aggregationToToday(energySource);
            }
            if (solarSource) {
                this.solarData = this.aggregationToToday(solarSource);
            }

            // Calculations and show balance chart
            if (this.energyData && this.solarData) {
                this.gridData = this.calculateBalance(this.energyData, this.solarData);
                this.balanceData = this.invert(this.gridData);
            }
            else {
                this.gridData = [];
                this.balanceData = [];
            }
            if (!energySource) {
                this.energyData = [];
            }
            if (!solarSource) {
                this.solarData = [];
            }
        }
        else {
            this.energyData = [];
            this.solarData = [];
            this.gridData = [];
            this.balanceData = [];
        }

        this.calculateSidebar();
        this.showIsland();
    }

    loadPriceData() {
        this.apxPriceData = []
        var currentDate = new Date();
        var startDate = new Date();
        // Actually showing both the first and last hour, so ensure the value of the first hour
        // is including by setting the start date before the first hour.
        startDate.setHours(currentDate.getHours() - 25);

        if (!this.globals.demoData) {
            //APXData from blobstorage
            let apxdata = this.energyDataService.apxActualData;
            if (apxdata != null) {
                for (var i = 0; i < apxdata.length; i++) {
                    let energy = apxdata[i];
                    var date = new Date(Date.parse(energy.date));
                    if (date >= startDate && date <= currentDate) {
                        // price data is in euros, showing it in eurocents
                        this.apxPriceData.push(parseFloat(energy.value) * 100);
                    }
                }
            }
        }
        else {
            //Demo data
            if (this.energyDataService.apxProfile) {
                for (var i = 0; i < this.energyDataService.apxProfile.data.apx.length; i++) {
                    let d = this.energyDataService.apxProfile.data.apx[i];
                    if ((i < 24 && d.biedUur >= currentDate.getHours()) || (i >= 24 && d.biedUur <= currentDate.getHours())) {
                        this.apxPriceData.push(d.prijsEuroPerMWh / 10);
                    }
                }
            }
        }

        this.priceChartData = [
            {
                data: this.apxPriceData,
                color: '#f2a200',
                title: 'APX prijs in eurocenten'
            },
            {
                data: this.islandPriceData,
                color: '#a5acb0',
                title: 'Gemiddelde eiland prijs in eurocenten'
            }
        ];
    }

    updateIslandChart(dataSet: number[], label: string, color: string) {
        this.energyChartData = [
            {
                data: dataSet,
                color: color,
                title: label
            }
        ];
    }

    aggregationToToday(jsonData: ProfileData[]): number[] {
        var result: number[] = [];
        var currentDate = new Date();
        var startDate = new Date();
        startDate.setHours(currentDate.getHours() - 24);

        for (var d of jsonData) {
            var date = new Date(Date.parse(d.date));
            if ((date.getMonth() == startDate.getMonth() && date.getDate() == startDate.getDate() && date.getHours() >= startDate.getHours()) ||
                (date.getMonth() == currentDate.getMonth() && date.getDate() == currentDate.getDate() && date.getHours() <= currentDate.getHours())) {
                result.push(parseFloat(d.value));
            }
        }

        return result;
    }

    initStaticData() {
        var currentDate = new Date();
        this.labelData = [];
        this.islandPriceData = [];

        for (var i = 0; i <= 24; i++) {
            var hour = i + currentDate.getHours();
            hour = hour > 23 ? hour - 24 : hour;
            var label = hour < 10 ? "0" + hour : "" + hour;
            label += ":00";
            this.labelData.push(label);
            this.islandPriceData.push(hour >= 7 && hour <= 23 ? 9 : 7);
        }
    }

    showConsumption() {
        if (this.currentIslandChart == "consumption") {
            this.showIsland();
        }
        else {
            this.currentIslandChart = "consumption";
            this.updateIslandChart(this.energyData, "Verbruik in kWh", '#a1c4d0');
        }
    }

    showSolar() {
        if (this.currentIslandChart == "solar") {
            this.showIsland();
        }
        else {
            this.currentIslandChart = "solar";
            this.updateIslandChart(this.solarData, "Opwek in kWh", '#f2a200');
        }
    }

    showGrid() {
        if (this.currentIslandChart == "grid") {
            this.showIsland();
        }
        else {
            this.currentIslandChart = "grid";
            this.updateIslandChart(this.gridData, "Net gebruik in kWh", '#f2a200');
        }
    }

    showBattery() {
        if (this.currentIslandChart == "battery") {
            this.showIsland();
        }
        else {
            this.currentIslandChart = "battery";
            this.updateIslandChart([
                0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
            ], "Opslag in kWh", '#f2a200');
        }
    }

    showIsland() {
        this.currentIslandChart = "island";
        this.updateIslandChart(this.balanceData, "Eiland balans in kWh", '#f2a200');
    }

    calculateBalance(energy: number[], solar: number[]) {
        var result: number[] = [];

        for (var i = 0; i < energy.length && i < solar.length; i++) {
            result.push(energy[i] - solar[i]);
        }

        return result;
    }

    invert(numbers: number[]) {
        var result: number[] = [];

        for (var val of numbers) {
            result.push(val * -1);
        }

        return result;
    }

    calculateSidebar() {
        let totalEnergy = this.energyData.reduce((a, b) => a + b, 0);
        let totalUsage = 0, totalProduced = 0,
            totalApxPrice = 0, totalIslandPrice = 0;

        for (let hour = 0; hour < this.gridData.length; hour++) {
            let val = this.gridData[hour];
            if (val > 0) {
                totalUsage += val;

                if (this.apxPriceData && this.apxPriceData.length > 0) {
                    totalApxPrice += this.apxPriceData[hour] * val;
                    totalIslandPrice += this.islandPriceData[hour] * val;
                }
            } else {
                totalProduced += val * -1;
            }
        }

        this.energyConsumed = totalUsage;
        this.energyProduced = totalProduced;

        if (totalEnergy > 0) {
            let renewableEnergy = totalEnergy - totalUsage;
            this.renewable = (renewableEnergy / totalEnergy * 100);
        }
        else {
            this.renewable = 0;
        }

        this.directApxBuy = (totalApxPrice / 100);
        this.potentialSavings = ((totalApxPrice - totalIslandPrice) / 100);
    }

    // Called when user switches euro to kwh or demo data to actual data
    @HostListener('window:refreshData')
    refreshData() {
        this.loadIslandData();
        this.loadPriceData();
    }

    mobileCheck() {
        let check = false;
        // From: http://detectmobilebrowsers.com/
        // https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser
        (function (a, b) {
            if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4)))
                check = true
        })(navigator.userAgent || navigator.vendor);
        return check;
    };
}



// WEBPACK FOOTER //
// ./ClientApp/app/pages/island-energy/island-energy.component.ts